import ComponentManager from '@/Management/ComponentManager';
import ViewExtensionViewModel from '@/ViewModels/Views/ViewExtensionViewModel';
import RADApplication from '@/Management/RADApplication';
import StorageHelper from '@/Utility/StorageHelper';
import NavigationService from '@/Management/NavigationService';
import ResourceManager from '@/Management/ResourceManager';
import * as Interfaces from '#/DefaultViewExtensionInterfaces';

import LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import AutoCompleteViewModelType from '@/ViewModels/Controls/AutoCompleteViewModel';
import ButtonViewModelType from '@/ViewModels/Controls/ButtonViewModel';
import MenuViewModelType from '@/ViewModels/Controls/MenuViewModel';
import MenuItemViewModelType from '@/ViewModels/Controls/MenuItemViewModel';
import OptionsLandschaftGroupViewModelType from '@/ViewModels/Controls/OptionsLandschaftGroupViewModel';
import OptionsLandschaftViewModelType from '@/ViewModels/Controls/OptionsLandschaftViewModel';
import Utilites from '@/Utility/Utilities';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';

export default class SettingsViewModel extends ViewExtensionViewModel {

    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelType>('ImageViewModel');
    private AutoCompleteViewModel = ComponentManager.getViewModel<typeof AutoCompleteViewModelType>('AutoCompleteViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelType>('ButtonViewModel');
    private MenuViewModel = ComponentManager.getViewModel<typeof MenuViewModelType>('MenuViewModel');
    private OptionsLandschaftGroupViewModel = ComponentManager.getViewModel<typeof OptionsLandschaftGroupViewModelType>('OptionsLandschaftGroupViewModel');
    private OptionsLandschaftViewModel = ComponentManager.getViewModel<typeof OptionsLandschaftViewModelType>('OptionsLandschaftViewModel');

    protected link: LinkViewModelType;
    protected textBox: AutoCompleteViewModelType;
    protected button: ButtonViewModelType;
    protected optionsLandschaft: OptionsLandschaftViewModelType;
    protected adminUserMenu: MenuViewModelType;
    protected isAdminMenuVisible: KnockoutObservable<boolean>;
    protected viewState: KnockoutComputed<string>;
    protected itemsDTO: KnockoutObservable<Interfaces.SettingsItemsDTO>;
    protected tempItems: KnockoutObservable<MenuViewModelType>;

    public constructor(params: any) {
        super(params);

        this.isAdminMenuVisible = this.observableOrDefault(false);
        this.viewState = StorageHelper.bind('SettingsViewState', 'alphabet');
        this.addComputed(this.viewState);
        this.initItems();
        this.initViewModels();

        this.defineComputed(() => {
            let itemsDTO = this.itemsDTO();
            let viewState = this.viewState();
            let filter = this.textBox.value().trim();
            let groups = this.optionsLandschaft.groups();
            // Falls sich �bersetzungen �ndern, muss die ganze View aufgrund der alphabetischen Gruppen neu gerendert werden
            let resources = ResourceManager.getInstance().resources();

            if (itemsDTO == null) {
                return;
            }

            for (let i = 0; i < groups.length; i++) {
                groups[i].dispose();
            }

            if (viewState === 'category') {
                groups = this.buildGroups(itemsDTO, filter, this.groupByCategories);
            } else {
                groups = this.buildGroups(itemsDTO, filter, this.groupByFirstLetter);
            }

            this.optionsLandschaft.groups(groups);
            this.optionsLandschaft.isBusy(false);
        });
    }

    private addThirdPartyItem(): void {
        ko.ignoreDependencies(() => {
            this.itemsDTO().Item.push(this.createThirdPartyItem());
        });
    }

    protected createThirdPartyItem(): Interfaces.SettingsItemDTO {
        const resolvedErgName = ResourceManager.getInstance().get('THIRD_PARTY_COMPONENTS');

        return {
            name: 'THIRD_PARTY',
            ergName: 'THIRD_PARTY_COMPONENTS',
            link: this.createThirdPartyLinkViewModel(),
            categories: resolvedErgName,
            tags: resolvedErgName
        }
    }

    private createThirdPartyLinkViewModel(): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'thirdparty.link',
            onClick: () => {
                let viewFactory = NavigationService.getInstance().loadViewFactoryIsolated('ThirdPartyComponentsView', {
                    onClose: () => {
                        NavigationService.getInstance().removeViewFactoryIsolated(viewFactory);
                    }
                }, true);
            }
        });
    }

    protected initViewModels(): void
    {
        this.initAdminUserMenu();
        this.initLink();
        this.initTextBox();
        this.initButton();
        this.initOptionsLandschaft();
    }

    protected initAdminUserMenu(): void
    {
        this.adminUserMenu = this.MenuViewModel.parseUserMenuTree();
    }

    protected initLink(): void
    {
        this.link = new this.LinkViewModel({
            parentLocator: 'settings.category',
            locator: 'sort',
            image: new this.ImageViewModel({
                source: this.defineComputed(() => {
                    return this.viewState() === 'category' ? GlobalConfiguration.inlineImages.ELEMENTS4 : GlobalConfiguration.inlineImages.SORT_AZ;
                })
            }),
            onClick: () => {
                let newViewState = this.viewState() === 'category' ? 'alphabet' : 'category';
                this.viewState(newViewState);
            }
        });
    }

    protected initTextBox(): void
    {
        this.textBox = new this.AutoCompleteViewModel({
            data: this.defineComputed(this.getAllSearchSuggestions.bind(this)),
            filter: 'contains',
            minLength: 3,
			parentLocator: 'settings',
            locator: 'searchfield',
            ariaLabel: ResourceManager.getInstance().get('OPTIONS_LANDSCAPE_SEARCHFIELD_LABEL')
        });
    }

    protected initButton(): void
    {
        this.button = new this.ButtonViewModel({
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.MAGNIFYING_GLASS,
                parentLocator: 'settings',
                locator: 'searchButton',
                alternativeText: new this.LabelViewModel({
                    ergName: 'SEARCH'
                })
            })
        });
    }

    protected initItems(): void
    {
        this.tempItems = this.observableOrDefault(this.MenuViewModel.parseUserMenuTree());
        this.itemsDTO = this.observableOrDefault({ Item: [] });

        ko.ignoreDependencies(() => {
            this.iterateItems(this.tempItems(), '');
        });

        this.addThirdPartyItem();
    }

    protected initOptionsLandschaft() {
        this.optionsLandschaft = new this.OptionsLandschaftViewModel({
            isBusy: true
        });
    }

    protected isContainedInString(searchString: string, valueString: string, delimiter: string): boolean {
        if (valueString == null || valueString.trim() == "") {
            return false;
        }

        if (!delimiter) {
            return false;
        }

        searchString = searchString.toLowerCase();
        valueString = valueString.trim().toLowerCase();

        if (searchString == valueString) {
            return true;
        }

        let values;

        if (valueString.indexOf(delimiter) == -1) {
            values = [valueString];
        }
        else {
            values = valueString.split(delimiter).map((v) => { return v.trim(); }).filter((v) => { return v != ''; });
        }

        return values.some((v) => { return v.includes(searchString); });
    }

    protected isContainedInItem(searchString: string, item: Interfaces.SettingsItemDTO): boolean {
        if (!searchString)
            return true;

        let name = item.name;
        let tags = item.tags;

        return this.isContainedInString(searchString, name, ' ') || this.isContainedInString(searchString, tags, ',');
    }

    protected sortCaseInsensitive(a: string, b: string): number {
        return a.toLowerCase().localeCompare(b.toLowerCase());
    }

    protected buildGroups(itemsDTO: Interfaces.SettingsItemsDTO, filter: string, groupProvider: (item: Interfaces.SettingsItemDTO) => Array<string>): Array<OptionsLandschaftGroupViewModelType> {
        let groups = [];
        let groupToItems = [];

        // Daten aus den Items aufbereiten und auf die Gruppen verteilen.
        for (let i = 0; i < itemsDTO.Item.length; i++) {
            let Item = itemsDTO.Item[i];
            let providedGroups = groupProvider(Item);

            if (providedGroups == null)
                continue;

            if (this.isContainedInItem(filter, Item)) {
                for (let j = 0; j < providedGroups.length; j++) {
                    let group = providedGroups[j];

                    if (groups.indexOf(group) === -1) {
                        groups.push(group);
                    }

                    if (groupToItems[group] == null) {
                        groupToItems[group] = [];
                    }

                    groupToItems[group].push(Item);
                }
            }
        }

        // Gruppen und innerhalb der Gruppen sortieren
        for (let i = 0; i < groups.length; i++) {
            let group = groups[i];
            if (groupToItems[group]) {
                // Items über den Anzeigenamen sortieren
                groupToItems[group].sort((a, b) => {
                    let result = this.sortCaseInsensitive(a.name, b.name);
                    // Wenn Namen gleich: Sortieren über ergName damit die Einträge immer die gleiche Reihenfolge haben
                    if(result === 0)
                        result = this.sortCaseInsensitive(a.ergName, b.ergName);
                    return result;
                });
            }
        }
        groups.sort(this.sortCaseInsensitive);

        // Die vorbereiteten Daten in ViewModels etc. umwandeln
        let groupViewModels = [];
        for (let i = 0; i < groups.length; i++) {
            let linkViewModels = [];
            const group = groups[i];

            for (let j = 0; j < groupToItems[group].length; j++) {
                const item = groupToItems[group][j];

                let labelParams: any = {};
                if (Utilites.isNullOrUndefinedOrBlank(item.ergName))
                    labelParams.caption = item.name;
                else
                    labelParams.ergName = item.ergName;

                let label = new this.LabelViewModel(labelParams);

                // Accessible Name mit Kategorie-Kontext, damit jeder Eintrag für Screen Reader eindeutig benannt ist
                const displayName = labelParams.ergName
                    ? ResourceManager.getInstance().get(labelParams.ergName) : labelParams.caption;
                const ariaLabel = (displayName && item.categories) ? `${displayName} (${item.categories})` : (displayName || '');

                if (item.link instanceof this.LinkViewModel) {
                    // Hier muss eine Kopie des LinkViewModels erstellt werden, da es sonst bei einer Umsortierung disposed wird
                    linkViewModels.push(new this.LinkViewModel({
                        onClick: item.link.onClick,
                        label: label,
                        ariaLabel: ariaLabel,
                        parentLocator: 'settings',
                        locator: item.ergName
                    }));
                }
                else {
                    linkViewModels.push(new this.LinkViewModel({
                        label: label,
                        ariaLabel: ariaLabel,
                        tooltip: new this.LabelViewModel({
                            ergName: item.tooltip
                        }),
                        onClick: (openInNeWindow) => {
                            NavigationService.getInstance().navigateToUrl(item.link['#cdata'] || item.link, null, null, null, null, openInNeWindow);
                        },
                        parentLocator: 'settings',
                        locator: item.ergName
                    }));
                }
            }


            groupViewModels[i] = new this.OptionsLandschaftGroupViewModel({
                isVisible: linkViewModels.length > 0,
                label: new this.LabelViewModel({
                    caption: group
                }),
                links: linkViewModels
            });
        }

        return groupViewModels;
    }

    protected groupByFirstLetter(item: Interfaces.SettingsItemDTO): Array<string> {
        let name = item.name;
        if (item.ergName)
            name = ResourceManager.getInstance().get(item.ergName);

        if (Utilites.isNullOrUndefined(name) || Utilites.isBlank(name))
            return null;

        return [name[0].toUpperCase()];
    }

    protected groupByCategories(item: Interfaces.SettingsItemDTO): Array<string> {
        return item.categories.replace(/, /g, ',').split(',');
    }

    protected getAllSearchSuggestions(): Array<string> {
        let itemsDTO = this.itemsDTO();
        let results = [];
        let resultSet = {};

        if (itemsDTO == null)
            return results;

        if (itemsDTO.Item == null)
            return results;

        let itemDTOs = itemsDTO.Item;

        for (let i = 0; i < itemDTOs.length; i++) {
            let itemDTO = itemDTOs[i];
            if (itemDTO == null)
                continue;

            let name = itemDTO.name;
            if (name != null) {
                name = name.trim();
                if (name != '' && !resultSet[name]) {
                    name = name.trim();
                    results.push(name);
                    resultSet[name] = true;
                }
            }

            let tags = itemDTO.tags;

            if (tags != null && tags.trim() != '') {
                let tagValues = tags.trim().split(',');
                for (let j = 0; j < tagValues.length; j++) {
                    let tagValue = tagValues[j].trim();
                    if (tagValue != '' && !resultSet[tagValue]) {
                        results.push(tagValue);
                        resultSet[tagValue] = true;
                    }
                }
            }
        }

        results.sort();

        return results;
    }

    private iterateItems(item: MenuViewModelType | MenuItemViewModelType, group: string) {
        let currentGroup = group;
        if (this.isNode(item) && currentGroup != '%%DUMMY%%') {
            let castedItem = item as MenuItemViewModelType;
            let name = '';
            let ergName = castedItem.link.label.ergName();
            if (castedItem.link.label.caption() != '')
                name = castedItem.link.label.caption();
            else
                name = ResourceManager.getInstance().get(castedItem.link.label.ergName());

            this.itemsDTO().Item.push({
                name: name,
                ergName: ergName,
                link: castedItem.link,
                categories: group,
                tags: ''
            });
        }
        else {
            if (!(item instanceof this.MenuViewModel)) {
                if (item.link.label.caption() != '')
                    currentGroup = item.link.label.caption();
                else
                    currentGroup = ResourceManager.getInstance().get(item.link.label.ergName())
            }
            else
                currentGroup = '%%DUMMY%%';

            for (let i = 0; i < item.items().length; i++) {
                this.iterateItems(item.items()[i], currentGroup);
            }
        }
    }

    private isNode(item: MenuViewModelType | MenuItemViewModelType) {
        if (!(item instanceof this.MenuViewModel) && (!item.items || item.items().length == 0)) {
            return true;
        }

        return false;
    }
}

ComponentManager.setViewModel('SettingsViewModel', SettingsViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');